import React, { useState } from 'react';
import { Col, Dropdown, Form, Row } from 'react-bootstrap';
import moment from 'moment';
import PageTitle from '../../../layouts/PageTitle';
import ListPage from '../../../layouts/page/ListPage';
import FilterAccordion from '../../../components/Common/Filter/FilterAccordion';
import CustomSelect from '../../../components/Forms/Element/CustomSelect';
import ModalPageEvent from '../../../components/Common/Modal/ModalPageEvent';
import MenuConfig from '../../../../constants/MenuConfig';
import SaaSBillingService from '../../../../services/superadmin/SaaSBillingService';
import { ApiError } from '../../../components/Common/ApiError';
import { successAlert, warnAlert, warnAlertYesOrNo } from '../../../components/alert/defaultAlert';
import { SaaSContractColumns } from './SaaSContractColumns';
import { SearchOperation } from '../../../../utils/ApiUtils';

const statusOptions = [
    { value: 'ACTIVE', label: 'Ativo' },
    { value: 'PENDING_PAYMENT', label: 'Aguardando pagamento' },
    { value: 'OVERDUE', label: 'Vencido' },
    { value: 'BLOCKED', label: 'Bloqueado' },
    { value: 'EXEMPT', label: 'Isento' },
    { value: 'CANCELED', label: 'Cancelado' },
];

const SaaSContractList = () => {
    const [selectedRows, setSelectedRows] = useState([]);
    const [showExemption, setShowExemption] = useState(false);
    const [exemption, setExemption] = useState({ reason: '', exemptUntil: '' });
    const [saving, setSaving] = useState(false);
    const [companyFilter, setCompanyFilter] = useState('');
    const [planFilter, setPlanFilter] = useState('');
    const [subscriptionFilter, setSubscriptionFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState(null);
    const [filters, setFilters] = useState([]);

    const search = () => {
        const next = [];
        if (companyFilter.trim()) next.push({ field: 'company.name', value: companyFilter.trim(), operation: SearchOperation.MATCH_WORDS });
        if (planFilter.trim()) next.push({ field: 'plan.name', value: planFilter.trim(), operation: SearchOperation.MATCH_WORDS });
        if (subscriptionFilter.trim()) next.push({ field: 'providerSubscriptionId', value: subscriptionFilter.trim(), operation: SearchOperation.MATCH });
        if (statusFilter) next.push({ field: 'contractStatus', value: statusFilter.value, operation: SearchOperation.EQUAL });
        setSelectedRows([]);
        setFilters(next);
    };

    const clearFilters = () => {
        setCompanyFilter('');
        setPlanFilter('');
        setSubscriptionFilter('');
        setStatusFilter(null);
        setSelectedRows([]);
        setFilters([]);
    };

    const contract = selectedRows[0];
    const companyName = contract?.company?.name || 'empresa';

    /**
     * Contrato não é apagado: ele é cancelado.
     *
     * Apagar deixaria as cobranças já emitidas sem origem e apagaria o histórico de
     * faturamento da empresa. O cancelamento encerra a assinatura no provedor e mantém
     * o registro, que é o que o financeiro precisa consultar depois.
     */
    const cancelContract = () => {
        if (!contract) {
            warnAlert('Selecione um contrato.');
            return;
        }

        warnAlertYesOrNo(
            `Cancelar o contrato de ${companyName}? A assinatura é encerrada no provedor de pagamento.`,
            () => {
                SaaSBillingService.cancelContract({ companyId: contract.company?.id })
                    .then(() => successAlert('Contrato cancelado.', () => window.location.reload()))
                    .catch((error) => ApiError(error));
            }
        );
    };

    /**
     * Isentar é o caminho para liberar quem está bloqueado com a fatura renegociada.
     *
     * Enquanto a isenção vigora, nenhum evento de cobrança altera o acesso, então o
     * próximo aviso de atraso não volta a bloquear a empresa. Ela vence sozinha na data
     * marcada e o contrato retoma o ciclo normal.
     */
    const openExemption = () => {
        if (!contract) {
            warnAlert('Selecione um contrato.');
            return;
        }

        // O vencimento seguinte é o prazo que o operador quase sempre quer, então ele já
        // vem preenchido e sobra só confirmar.
        setExemption({
            reason: contract.exemptionReason || '',
            exemptUntil: contract.exemptUntil || contract.nextDueDate || ''
        });
        setShowExemption(true);
    };

    const confirmExemption = () => {
        setSaving(true);
        SaaSBillingService.exemptContract({
            companyId: contract.company?.id,
            reason: exemption.reason,
            exemptUntil: exemption.exemptUntil
        })
            .then(() => successAlert(
                `Acesso liberado até ${moment(exemption.exemptUntil).format('DD/MM/YYYY')}.`,
                () => window.location.reload()))
            .catch((error) => ApiError(error))
            .finally(() => setSaving(false));
    };

    /** Encerra a isenção antes do prazo: o contrato volta a ser avaliado pelo pagamento. */
    const revokeExemption = () => {
        if (!contract) {
            warnAlert('Selecione um contrato.');
            return;
        }

        warnAlertYesOrNo(
            `Encerrar a isenção de ${companyName}? O contrato volta a ser avaliado pelas cobranças.`,
            () => {
                SaaSBillingService.revokeExemption({ companyId: contract.company?.id })
                    .then(() => successAlert('Isenção encerrada.', () => window.location.reload()))
                    .catch((error) => ApiError(error));
            }
        );
    };

    return <>
        <PageTitle activeMenu="Contratos SaaS" motherMenu="Cobrança SaaS" />
        <FilterAccordion title="filters" onSearch={search} onClear={clearFilters} centerButtons={false}>
            <Row className="g-2 align-items-end">
                <Col lg={3} md={6}><Form.Label htmlFor="contract-company">Empresa</Form.Label><Form.Control id="contract-company" value={companyFilter} onChange={event => setCompanyFilter(event.target.value)} placeholder="Nome da empresa" /></Col>
                <Col lg={3} md={6}><Form.Label htmlFor="contract-plan">Plano</Form.Label><Form.Control id="contract-plan" value={planFilter} onChange={event => setPlanFilter(event.target.value)} placeholder="Nome do plano" /></Col>
                <Col lg={3} md={6}><Form.Label htmlFor="contract-status">Situação</Form.Label><CustomSelect inputId="contract-status" value={statusFilter} options={statusOptions} onChange={setStatusFilter} placeholder="Todas as situações" isClearable /></Col>
                <Col lg={3} md={6}><Form.Label htmlFor="contract-subscription">Assinatura Asaas</Form.Label><Form.Control id="contract-subscription" value={subscriptionFilter} onChange={event => setSubscriptionFilter(event.target.value)} placeholder="ID da assinatura" /></Col>
            </Row>
        </FilterAccordion>
        <ListPage
            showPageTitle={false}
            activeMenu="Contratos SaaS"
            motherMenu="Cobrança SaaS"
            path="/saas-billing/contracts"
            route={MenuConfig.superadmin.saas_billing.contractsRoute}
            columns={SaaSContractColumns}
            filters={filters}
            hideInactiveToggle
            setSelectedRows={setSelectedRows}
            hideRemove
            customButtons={<>
                <Dropdown.Item onClick={openExemption}>Isentar (liberar acesso)</Dropdown.Item>
                <Dropdown.Item onClick={revokeExemption}>Encerrar isenção</Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={cancelContract}>Cancelar contrato</Dropdown.Item>
            </>}
        />

        <ModalPageEvent
            title="Liberar acesso por isenção"
            show={showExemption}
            setShow={setShowExemption}
            size="md"
            btnOnClick={confirmExemption}
            btnText="Liberar acesso"
            // Motivo e prazo são obrigatórios no servidor; barrar aqui evita o ida e volta.
            btnDisabled={saving || !exemption.reason?.trim() || !exemption.exemptUntil}
            child={
                <div className="saas-exemption">
                    <p className="text-muted fs-13">
                        {companyName} volta a usar o sistema até a data escolhida, mesmo com fatura em
                        aberto. Nenhum aviso de atraso bloqueia o acesso nesse período, e a liberação
                        termina sozinha no dia marcado.
                    </p>
                    <Row className="g-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Liberado até</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={exemption.exemptUntil || ''}
                                    onChange={(event) => setExemption(current =>
                                        ({ ...current, exemptUntil: event.target.value }))}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label>Motivo</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    maxLength={255}
                                    placeholder="Ex.: fatura renegociada para 10/09"
                                    value={exemption.reason}
                                    onChange={(event) => setExemption(current =>
                                        ({ ...current, reason: event.target.value }))}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </div>
            }
        />
    </>;
};

export default SaaSContractList;
